import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BrandMark, EventismCredit } from "@/components/Brand";
import { toast } from "sonner";

const TEMPLATES = {
  "graduation-2026": {
    kicker: "Graduation 2026 Pack",
    headline: "50-Guest Backyard Grad Party — Tent, Tables, Chairs Included",
    sub: "The proven Park Hill kit. Reserve 14+ days out — May tents go fast.",
    bullets: [
      "10×10 Pop-Up Tent (Denver-code sandbag weights included)",
      "4 × 8 ft long tables for the buffet line",
      "50 black folding chairs",
      "8 qt chafing set to keep the BBQ hot",
    ],
    price_callout: "$249 + tax · refundable $50 hold",
    image:
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1600&q=80",
  },
  "wedding-backyard": {
    kicker: "Backyard Wedding Pack",
    headline: "DIY Backyard Wedding — Self-Service, Pod-Powered",
    sub: "Reception kit for 60. Pop the tent on the lawn, layer the linens, eat under the stars.",
    bullets: [
      "Two 10×10 tents (sidewalls add-on available)",
      "8 long farm-style tables with linens",
      "60 folding chairs",
      "Cabaret tables for cocktail hour",
    ],
    price_callout: "From $389 + tax",
    image:
      "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80",
  },
  "bbq-block-party": {
    kicker: "BBQ Block Party Special",
    headline: "Block Party Pack — One Pod, One Cul-de-Sac, One Hero Host",
    sub: "Tent, chafers, long tables, 30 chairs. Walk to the Park Hill Pod, grab it, return it.",
    bullets: [
      "10×10 Pop-Up Tent for the food line",
      "2 × 8 qt chafing sets (burgers + sides)",
      "Long tables + 30 folding chairs",
      "Optional linens for the dessert table",
    ],
    price_callout: "$179 + tax · 2-day rental saves $35",
    image:
      "https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=1600&q=80",
  },
};

export default function LandingPage() {
  const { slug } = useParams();
  const tpl = TEMPLATES[slug];
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!tpl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-slate">
        <p className="text-brand-blue font-black uppercase">Landing page not found</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/leads", {
        email,
        source: `lp:${slug}`,
        landing_slug: slug,
        referrer: document.referrer || null,
      });
      setDone(true);
      toast.success("Got it — check your inbox shortly.");
    } catch {
      toast.error("Couldn't save. Try again or text us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-slate" data-testid={`landing-${slug}`}>
      <header className="px-6 py-5 flex items-center justify-between border-b border-slate-100 bg-white">
        <BrandMark />
        <Link
          to="/#inventory"
          className="text-[10px] font-black uppercase tracking-widest text-brand-blue hover:text-brand-red"
        >
          Browse the Pod →
        </Link>
      </header>

      <section className="relative overflow-hidden">
        <img
          src={tpl.image}
          alt={tpl.headline}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-28">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">
            {tpl.kicker}
          </p>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tightest text-brand-blue leading-[0.95]">
            {tpl.headline}
          </h1>
          <p className="text-base md:text-lg text-slate-700 font-bold mt-5 max-w-2xl">{tpl.sub}</p>

          <div className="mt-8 bg-white rounded-[28px] border border-slate-100 shadow-xl p-6 md:p-8 max-w-xl">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-4">
              What's Included
            </p>
            <ul className="space-y-2">
              {tpl.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700 font-bold">
                  <i className="fas fa-circle-check text-brand-blue mt-1" /> {b}
                </li>
              ))}
            </ul>
            <p className="text-2xl font-black uppercase text-brand-blue tracking-tightest mt-6">
              {tpl.price_callout}
            </p>

            {done ? (
              <div
                className="mt-6 bg-brand-blue text-white rounded-2xl p-5 text-center"
                data-testid="landing-success"
              >
                <i className="fas fa-circle-check text-3xl text-brand-gold mb-2" />
                <p className="font-black uppercase tracking-tightest">You're on the list!</p>
                <p className="text-[11px] font-bold text-white/80 mt-1">
                  We'll text + email when this pack is bookable for your weekend.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 flex flex-col gap-2.5" data-testid="landing-form">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl border border-slate-200 px-4 py-3.5 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
                  data-testid="landing-email"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary py-4 rounded-xl font-black uppercase tracking-widest text-xs disabled:opacity-50"
                  data-testid="landing-submit"
                >
                  {busy ? (
                    <><i className="fas fa-spinner fa-spin mr-2" /> Saving</>
                  ) : (
                    <>Reserve My Date <i className="fas fa-arrow-right ml-2" /></>
                  )}
                </button>
                <p className="text-[10px] font-bold text-slate-400 text-center mt-1">
                  Free to ask. No card needed. Pod is at 3475 Holly St, Park Hill.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="px-6 py-12 text-center border-t border-slate-100 bg-white">
        <BrandMark />
        <p className="text-[10px] font-bold text-slate-400 mt-3">
          3475 Holly St, Denver CO 80207 · 720-907-4044
        </p>
        <div className="mt-3">
          <EventismCredit />
        </div>
      </footer>
    </div>
  );
}
